import apiClient from './axios';
import { ApiResponse } from '../types';

export type VendorStatus = 'pending' | 'active' | 'suspended';

export interface VendorOnboardingAdmin {
  id: string;
  firstName: string;
  lastName: string;
  profilePhoto: string | null;
}

export interface Vendor {
  id: string;
  slug: string;
  name: string;
  ownerEmail: string | null;
  ownerPhone: string | null;
  location: string | null;
  pickupAddressLine: string | null;
  pickupCity: string | null;
  pickupState: string | null;
  pickupPhone: string | null;
  pickupLat: number | null;
  pickupLng: number | null;
  pickupGooglePlaceId: string | null;
  tagline: string | null;
  avatarUrl: string | null;
  freeDelivery: boolean;
  status: VendorStatus;
  payoutBalance: number;
  rating: number;
  reviewsCount: number;
  /** Null for vendors created before attribution was introduced. */
  onboardedBy: VendorOnboardingAdmin | null;
  createdAt: string;
  /** Present only on the create-vendor response. Indicates whether
   *  the backend was able to create the VendorUser login row and
   *  send the welcome email. Null when the admin skipped login
   *  provisioning (e.g. didn't supply ownerEmail). */
  loginProvisioned?: { emailSent: boolean } | null;
}

export interface CreateVendorDto {
  name: string;
  slug?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  location?: string;
  pickupAddressLine?: string | null;
  pickupCity?: string | null;
  pickupState?: string | null;
  pickupPhone?: string | null;
  pickupLat?: number | null;
  pickupLng?: number | null;
  pickupGooglePlaceId?: string | null;
  tagline?: string;
  avatarUrl?: string;
  freeDelivery?: boolean;
  /** When true (default), backend also creates the VendorUser login
   *  + emails a one-time temp password. */
  provisionLogin?: boolean;
}

export interface UpdateVendorDto {
  name?: string;
  ownerEmail?: string | null;
  ownerPhone?: string | null;
  location?: string | null;
  pickupAddressLine?: string | null;
  pickupCity?: string | null;
  pickupState?: string | null;
  pickupPhone?: string | null;
  pickupLat?: number | null;
  pickupLng?: number | null;
  pickupGooglePlaceId?: string | null;
  tagline?: string | null;
  avatarUrl?: string | null;
  freeDelivery?: boolean;
  status?: VendorStatus;
  payoutAccountNumber?: string | null;
  payoutBankCode?: string | null;
  payoutAccountName?: string | null;
}

export type ProductStatus = 'active' | 'draft' | 'archived';
export type ProductAttributeInputType =
  | 'text'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'date';

export interface ProductAttributeDefinition {
  id: string;
  categoryId: string;
  subCategoryId: string | null;
  key: string;
  label: string;
  inputType: ProductAttributeInputType;
  options: string[];
  required: boolean;
  aiHint: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminProduct {
  id: string;
  name: string;
  brand: string | null;
  price: number;
  imageUrl: string | null;
  categoryId: string | null;
  subCategoryId: string | null;
  attributes: Record<string, unknown>;
  featured: boolean;
  status: ProductStatus;
  stockQty: number | null;
  rating: number;
  ratingsCount: number;
  reviewCount: number;
  createdAt: string;
}

export interface EarningsSummary {
  payoutBalance: number;
  lockedInEscrow: number;
  pendingPayouts: number;
  lifetimeReleased: number;
  releasedLast7Days: number;
  releasedLast30Days: number;
  hasPayoutBank: boolean;
}

export type OrderStatus =
  | 'placed'
  | 'processing'
  | 'shipped'
  | 'out_for_delivery'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'disputed';

export interface AdminOrder {
  id: string;
  reference: string;
  userId: string;
  vendorId: string;
  vendorName?: string;
  status: OrderStatus;
  subtotal: number;
  shipping: number;
  total: number;
  placedAt: string;
  items: {
    productId: string;
    productName: string;
    productImageUrl: string | null;
    qty: number;
    unitPrice: number;
  }[];
}

export type PayoutStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface VendorPayout {
  id: string;
  vendorId: string;
  amount: number;
  status: PayoutStatus;
  bankCode: string | null;
  accountNumber: string | null;
  accountName: string | null;
  reference: string;
  providerReference: string | null;
  failureReason: string | null;
  processedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  createdAt: string;
}

export interface VendorDetail {
  vendor: Vendor;
  earnings: EarningsSummary;
  stats: {
    totalOrders: number;
    totalProducts: number;
    activeProducts: number;
    featuredProducts: number;
  };
  recentOrders: AdminOrder[];
  payouts: VendorPayout[];
  products: AdminProduct[];
}

export interface AdminSubCategory {
  id: string;
  categoryId: string;
  slug: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

/** What kind of store a category describes. Restaurants pick from
 *  `restaurant` categories when they set up; the marketplace and vendor app
 *  only ever show `retail` ones. Older APIs send nothing, which is retail. */
export type CategoryStoreType = 'retail' | 'restaurant';

export interface AdminCategory {
  id: string;
  slug: string;
  name: string;
  storeType?: CategoryStoreType;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  subCategories: AdminSubCategory[];
  attributeDefinitions: ProductAttributeDefinition[];
}

export interface CreateCategoryDto {
  name: string;
  slug?: string;
  imageUrl?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  storeType?: CategoryStoreType;
}

export type UpdateCategoryDto = Partial<CreateCategoryDto>;

export interface CreateSubCategoryDto {
  name: string;
  slug?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export type UpdateSubCategoryDto = Partial<CreateSubCategoryDto>;

export interface CreateAttributeDefinitionDto {
  categoryId: string;
  subCategoryId?: string | null;
  key: string;
  label: string;
  inputType: ProductAttributeInputType;
  options?: string[];
  required?: boolean;
  aiHint?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export type UpdateAttributeDefinitionDto =
  Partial<CreateAttributeDefinitionDto>;

export const shopApi = {
  listVendors: async (opts: { status?: VendorStatus; q?: string } = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    const { data } = await apiClient.get<ApiResponse<Vendor[]>>(
      `/admin/shop/vendors${params.toString() ? `?${params.toString()}` : ''}`,
    );
    return data.data;
  },

  getVendor: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<Vendor>>(
      `/admin/shop/vendors/${id}`,
    );
    return data.data;
  },

  createVendor: async (dto: CreateVendorDto) => {
    const { data } = await apiClient.post<ApiResponse<Vendor>>(
      '/admin/shop/vendors',
      dto,
    );
    return data.data;
  },

  updateVendor: async (id: string, patch: UpdateVendorDto) => {
    const { data } = await apiClient.patch<ApiResponse<Vendor>>(
      `/admin/shop/vendors/${id}`,
      patch,
    );
    return data.data;
  },

  deleteVendor: async (id: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/admin/shop/vendors/${id}`,
    );
    return data.data;
  },

  /** Regenerate the temp password + re-email it. Works whether or not
   *  the vendor already has a VendorUser row (creates one if missing). */
  resetVendorLogin: async (id: string) => {
    const { data } = await apiClient.post<
      ApiResponse<{ emailSent: boolean }>
    >(`/admin/shop/vendors/${id}/reset-login`);
    return data.data;
  },

  /** Full vendor-detail aggregate for the "view vendor as a page" screen. */
  getVendorDetail: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<VendorDetail>>(
      `/admin/shop/vendors/${id}/detail`,
    );
    return data.data;
  },

  activateVendor: async (id: string) => {
    const { data } = await apiClient.post<ApiResponse<Vendor>>(
      `/admin/shop/vendors/${id}/activate`,
    );
    return data.data;
  },

  suspendVendor: async (id: string) => {
    const { data } = await apiClient.post<ApiResponse<Vendor>>(
      `/admin/shop/vendors/${id}/suspend`,
    );
    return data.data;
  },

  setProductFeatured: async (productId: string, featured: boolean) => {
    const { data } = await apiClient.patch<ApiResponse<AdminProduct>>(
      `/admin/shop/products/${productId}/featured`,
      { featured },
    );
    return data.data;
  },

  searchProducts: async (search: string, limit = 10) => {
    const { data } = await apiClient.get<ApiResponse<AdminProduct[]>>(
      '/admin/shop/products',
      { params: { search, limit } },
    );
    return data.data;
  },

  // ─── Categories + subcategories ──────────────────────────────────────────

  listCategories: async () => {
    const { data } = await apiClient.get<ApiResponse<AdminCategory[]>>(
      '/admin/shop/categories',
    );
    return data.data;
  },

  createCategory: async (dto: CreateCategoryDto) => {
    const { data } = await apiClient.post<ApiResponse<AdminCategory>>(
      '/admin/shop/categories',
      dto,
    );
    return data.data;
  },

  updateCategory: async (id: string, patch: UpdateCategoryDto) => {
    const { data } = await apiClient.patch<ApiResponse<AdminCategory>>(
      `/admin/shop/categories/${id}`,
      patch,
    );
    return data.data;
  },

  deleteCategory: async (id: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/admin/shop/categories/${id}`,
    );
    return data.data;
  },

  createSubCategory: async (categoryId: string, dto: CreateSubCategoryDto) => {
    const { data } = await apiClient.post<ApiResponse<AdminSubCategory>>(
      `/admin/shop/categories/${categoryId}/subcategories`,
      dto,
    );
    return data.data;
  },

  updateSubCategory: async (id: string, patch: UpdateSubCategoryDto) => {
    const { data } = await apiClient.patch<ApiResponse<AdminSubCategory>>(
      `/admin/shop/subcategories/${id}`,
      patch,
    );
    return data.data;
  },

  deleteSubCategory: async (id: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/admin/shop/subcategories/${id}`,
    );
    return data.data;
  },

  createAttributeDefinition: async (dto: CreateAttributeDefinitionDto) => {
    const { data } = await apiClient.post<
      ApiResponse<ProductAttributeDefinition>
    >('/admin/shop/product-attribute-definitions', dto);
    return data.data;
  },

  updateAttributeDefinition: async (
    id: string,
    patch: UpdateAttributeDefinitionDto,
  ) => {
    const { data } = await apiClient.patch<
      ApiResponse<ProductAttributeDefinition>
    >(`/admin/shop/product-attribute-definitions/${id}`, patch);
    return data.data;
  },

  deleteAttributeDefinition: async (id: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/admin/shop/product-attribute-definitions/${id}`,
    );
    return data.data;
  },
};
